
import React, { useState } from 'react'
import { Plus, Eye, Edit, Trash2, Users, Calendar, DollarSign, TrendingUp, Search, Filter, CreditCard, MapPin, UserX, UserCheck } from 'lucide-react'
import { useFuncionarios } from '../hooks/useFuncionarios'
import MetricsCard from '../components/MetricsCard'
import FuncionarioForm from '../components/FuncionarioForm'
import DadosBancariosForm from '../components/DadosBancariosForm'
import FeriasForm from '../components/FeriasForm'
import HistoricoAlocacoes from '../components/HistoricoAlocacoes'

const Funcionarios: React.FC = () => {
  const {
    funcionarios,
    loading,
    metrics,
    createFuncionario,
    updateFuncionario,
    deleteFuncionario,
    getAlocacoesByFuncionario,
    getCargosDisponiveis
  } = useFuncionarios()

  const [showFuncionarioForm, setShowFuncionarioForm] = useState(false)
  const [showDadosBancarios, setShowDadosBancarios] = useState(false)
  const [showFerias, setShowFerias] = useState(false)
  const [showHistorico, setShowHistorico] = useState(false)
  const [editingFuncionario, setEditingFuncionario] = useState<any>(null)
  const [selectedFuncionario, setSelectedFuncionario] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [cargoFilter, setCargoFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Filter funcionários
  const filteredFuncionarios = funcionarios.filter(funcionario => {
    const matchesSearch = funcionario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         funcionario.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         funcionario.cargo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         funcionario.funcao.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCargo = cargoFilter === 'all' || funcionario.cargo === cargoFilter
    const matchesStatus = statusFilter === 'all' || funcionario.status === statusFilter
    return matchesSearch && matchesCargo && matchesStatus
  })

  // Calculate percentage change
  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous) * 100
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo': return 'bg-green-100 text-green-800'
      case 'ferias': return 'bg-blue-100 text-blue-800'
      case 'inativo': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ativo': return 'Ativo'
      case 'ferias': return 'Em Férias'
      case 'inativo': return 'Inativo'
      default: return status
    }
  }

  // Get remuneração label
  const getRemuneracaoLabel = (funcionario: any) => {
    const unidadeLabels = {
      hora: '/hora',
      dia: '/dia',
      mes: '/mês'
    }
    return `${formatCurrency(funcionario.valorRemuneracao)}${unidadeLabels[funcionario.unidadeRemuneracao as keyof typeof unidadeLabels]}`
  }

  // Handle delete
  const handleDelete = async (id: string, nome: string) => {
    if (confirm(`Tem certeza que deseja excluir o funcionário "${nome}"?`)) {
      await deleteFuncionario(id)
    }
  }

  // Handle dados bancários
  const handleDadosBancarios = (funcionario: any) => {
    setSelectedFuncionario(funcionario)
    setShowDadosBancarios(true)
  }

  // Handle férias
  const handleFerias = (funcionario: any) => {
    setSelectedFuncionario(funcionario)
    setShowFerias(true)
  }

  // Handle histórico
  const handleHistorico = (funcionario: any) => {
    setSelectedFuncionario(funcionario)
    setShowHistorico(true)
  }

  // Handle toggle status
  const handleToggleStatus = async (funcionario: any) => {
    const newStatus = funcionario.status === 'ativo' ? 'inativo' : 'ativo'
    await updateFuncionario(funcionario._id, { status: newStatus })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando funcionários...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Funcionários</h1>
              <p className="text-gray-600 mt-1">Gestão completa de funcionários e contratos</p>
            </div>
            <button
              onClick={() => setShowFuncionarioForm(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
            >
              <Plus size={20} />
              Novo Funcionário
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Métricas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <MetricsCard
            title="Total de Funcionários"
            value={metrics.totalFuncionarios}
            icon={Users}
            color="#3B82F6"
            description={`${calculatePercentageChange(metrics.totalFuncionarios, metrics.totalFuncionariosAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.totalFuncionarios, metrics.totalFuncionariosAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Em Férias"
            value={metrics.quantidadeFerias}
            icon={Calendar}
            color="#F59E0B"
            description={`${calculatePercentageChange(metrics.quantidadeFerias, metrics.quantidadeFeriasAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.quantidadeFerias, metrics.quantidadeFeriasAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Ativos"
            value={metrics.totalAtivos}
            icon={UserCheck}
            color="#10B981"
            description={`${calculatePercentageChange(metrics.totalAtivos, metrics.totalAtivosAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.totalAtivos, metrics.totalAtivosAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Folha Mensal"
            value={formatCurrency(metrics.folhaMensal)}
            icon={DollarSign}
            color="#059669"
            description={`${calculatePercentageChange(metrics.folhaMensal, metrics.folhaMensalAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.folhaMensal, metrics.folhaMensalAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Média Salarial"
            value={formatCurrency(metrics.mediaSalario)}
            icon={TrendingUp}
            color="#8B5CF6"
            description={`${calculatePercentageChange(metrics.mediaSalario, metrics.mediaSalarioAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.mediaSalario, metrics.mediaSalarioAnterior).toFixed(1)}% vs mês anterior`}
          />
        </div>

        {/* Filtros e Busca */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por nome, e-mail, cargo ou função..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={20} className="text-gray-400" />
              <select
                value={cargoFilter}
                onChange={(e) => setCargoFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos os Cargos</option>
                {getCargosDisponiveis().map(cargo => (
                  <option key={cargo} value={cargo}>{cargo}</option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Todos os Status</option>
                <option value="ativo">Ativos</option>
                <option value="ferias">Em Férias</option>
                <option value="inativo">Inativos</option>
              </select>
            </div>
          </div>
        </div>

        {/* Lista de Funcionários */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Lista de Funcionários ({filteredFuncionarios.length})
            </h2>
          </div>

          {filteredFuncionarios.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum funcionário encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || cargoFilter !== 'all' || statusFilter !== 'all'
                  ? 'Tente ajustar os filtros de busca'
                  : 'Comece cadastrando seu primeiro funcionário'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cargo/Função
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Remuneração
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Banco de Horas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFuncionarios.map((funcionario) => (
                    <tr key={funcionario._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{funcionario.nome}</div>
                          <div className="text-sm text-gray-500">Admissão: {formatDate(funcionario.dataAdmissao)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{funcionario.cargo}</div>
                          <div className="text-sm text-gray-500">{funcionario.funcao}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">{funcionario.telefone}</div>
                          <div className="text-sm text-gray-500">{funcionario.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getRemuneracaoLabel(funcionario)}
                          </div>
                          <div className="text-sm text-gray-500">Pag. dia {funcionario.diaPagamento}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${
                          funcionario.bancoHoras > 0 ? 'text-green-600' : 
                          funcionario.bancoHoras < 0 ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {funcionario.bancoHoras > 0 ? '+' : ''}{funcionario.bancoHoras}h
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(funcionario.status)}`}>
                          {getStatusLabel(funcionario.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingFuncionario(funcionario)}
                            className="text-yellow-600 hover:text-yellow-900 p-1 rounded"
                            title="Editar"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDadosBancarios(funcionario)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Dados Bancários"
                          >
                            <CreditCard size={16} />
                          </button>
                          <button
                            onClick={() => handleFerias(funcionario)}
                            className="text-purple-600 hover:text-purple-900 p-1 rounded"
                            title="Programar Férias"
                          >
                            <Calendar size={16} />
                          </button>
                          <button
                            onClick={() => handleHistorico(funcionario)}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Histórico de Alocações"
                          >
                            <MapPin size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(funcionario)}
                            className={`${funcionario.status === 'ativo' ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'} p-1 rounded`}
                            title={funcionario.status === 'ativo' ? 'Inativar' : 'Ativar'}
                          >
                            {funcionario.status === 'ativo' ? <UserX size={16} /> : <UserCheck size={16} />}
                          </button>
                          <button
                            onClick={() => handleDelete(funcionario._id, funcionario.nome)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <FuncionarioForm
        isOpen={showFuncionarioForm}
        onClose={() => setShowFuncionarioForm(false)}
        onSubmit={createFuncionario}
        title="Cadastrar Novo Funcionário"
      />

      <FuncionarioForm
        isOpen={!!editingFuncionario}
        onClose={() => setEditingFuncionario(null)}
        onSubmit={(data) => updateFuncionario(editingFuncionario._id, data)}
        initialData={editingFuncionario}
        title="Editar Funcionário"
      />

      <DadosBancariosForm
        isOpen={showDadosBancarios}
        onClose={() => {
          setShowDadosBancarios(false)
          setSelectedFuncionario(null)
        }}
        onSubmit={(data) => updateFuncionario(selectedFuncionario._id, data)}
        funcionario={selectedFuncionario}
      />

      <FeriasForm
        isOpen={showFerias}
        onClose={() => {
          setShowFerias(false)
          setSelectedFuncionario(null)
        }}
        onSubmit={(data) => updateFuncionario(selectedFuncionario._id, data)}
        funcionario={selectedFuncionario}
      />

      <HistoricoAlocacoes
        isOpen={showHistorico}
        onClose={() => {
          setShowHistorico(false)
          setSelectedFuncionario(null)
        }}
        funcionario={selectedFuncionario}
        alocacoes={selectedFuncionario ? getAlocacoesByFuncionario(selectedFuncionario._id) : []}
      />
    </div>
  )
}

export default Funcionarios
